import React, { useState, useEffect } from 'react';
import { db } from '../../../services/firebase';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../../../hooks/AuthContext';
import { FiLink, FiCopy, FiEye, FiEyeOff, FiRefreshCw } from 'react-icons/fi';
import toast from 'react-hot-toast';

const API_URL =
  'https://us-central1-gestor-de-respuestas-hingenia.cloudfunctions.net/gestorWpApi';
const CONFIG_DOC = 'wp_integration';
const SUPER_ADMIN_EMAIL = 'vcecursos@gmail.com';

const WordPressConnectionCard = () => {
  const { user } = useAuth();
  const [apiKey, setApiKey] = useState('');
  const [updatedAt, setUpdatedAt] = useState(null);
  const [updatedBy, setUpdatedBy] = useState('');
  const [loading, setLoading] = useState(true);
  const [showKey, setShowKey] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const isAdmin = user?.role === 'admin';
  const isSuperAdmin = user?.email === SUPER_ADMIN_EMAIL;

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'app_config', CONFIG_DOC));
        if (snap.exists()) {
          const data = snap.data();
          setApiKey(data.apiKey || '');
          setUpdatedAt(data.updatedAt?.toDate?.() || null);
          setUpdatedBy(data.updatedBy || '');
        }
      } catch (err) {
        console.error('Error al leer la config de WordPress:', err);
      }
      setLoading(false);
    })();
  }, []);

  if (!isAdmin) return null;

  const copy = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copiada`);
    } catch {
      toast.error('No se pudo copiar');
    }
  };

  const generateKey = () => {
    const bytes = crypto.getRandomValues(new Uint8Array(24));
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  };

  const handleRegenerate = async () => {
    setConfirmOpen(false);
    setRegenerating(true);
    const newKey = generateKey();
    try {
      await setDoc(
        doc(db, 'app_config', CONFIG_DOC),
        {
          apiKey: newKey,
          note: 'Clave para el endpoint gestorWpApi consumido por el plugin de WordPress',
          updatedAt: serverTimestamp(),
          updatedBy: user?.email || '',
        },
        { merge: true }
      );
      setApiKey(newKey);
      setUpdatedAt(new Date());
      setUpdatedBy(user?.email || '');
      setShowKey(true);
      toast.success('Clave nueva generada. Pégala en el plugin de WordPress.');
    } catch (err) {
      if (err?.code === 'permission-denied') {
        toast.error('Solo el super-admin (vcecursos@gmail.com) puede regenerar la clave.');
      } else {
        console.error('Error al guardar la clave:', err);
        toast.error('Error al guardar la clave');
      }
    }
    setRegenerating(false);
  };

  const maskedKey = apiKey ? '•'.repeat(Math.min(apiKey.length, 32)) : '';

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <h3 className="text-sm font-bold text-gray-800 mb-1 flex items-center gap-2">
        <FiLink size={14} className="text-indigo-500" /> Conexión con WordPress
      </h3>
      <p className="text-xs text-gray-500 mb-4">
        Datos que el plugin de WordPress usa para traer las fechas de las masterclasses.
      </p>

      {loading ? (
        <p className="text-xs text-gray-400">Cargando…</p>
      ) : (
        <div className="space-y-4">
          {/* URL */}
          <div>
            <label className="text-xs font-semibold text-gray-600">URL de la API</label>
            <div className="flex items-center gap-2 mt-1">
              <input
                readOnly
                value={API_URL}
                className="flex-1 text-xs bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-gray-700 font-mono"
              />
              <button
                onClick={() => copy(API_URL, 'URL')}
                className="p-2 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg"
                title="Copiar URL"
              >
                <FiCopy size={16} />
              </button>
            </div>
          </div>

          {/* API Key */}
          <div>
            <label className="text-xs font-semibold text-gray-600">API Key</label>
            <div className="flex items-center gap-2 mt-1">
              <input
                readOnly
                value={apiKey ? (showKey ? apiKey : maskedKey) : 'No configurada'}
                className="flex-1 text-xs bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-gray-700 font-mono"
              />
              <button
                onClick={() => setShowKey((s) => !s)}
                disabled={!apiKey}
                className="p-2 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg disabled:opacity-40"
                title={showKey ? 'Ocultar' : 'Mostrar'}
              >
                {showKey ? <FiEyeOff size={16} /> : <FiEye size={16} />}
              </button>
              <button
                onClick={() => copy(apiKey, 'API Key')}
                disabled={!apiKey}
                className="p-2 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg disabled:opacity-40"
                title="Copiar API Key"
              >
                <FiCopy size={16} />
              </button>
            </div>
          </div>

          {/* Footer */}
          <p className="text-xs text-gray-400">
            {updatedAt
              ? `Última actualización: ${updatedAt.toLocaleString('es-PE')}${
                  updatedBy ? ` por ${updatedBy}` : ''
                }`
              : 'Sin registro de actualización'}
          </p>

          {/* Regenerate */}
          <div>
            <button
              onClick={() => setConfirmOpen(true)}
              disabled={!isSuperAdmin || regenerating}
              className="text-xs px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
            >
              <FiRefreshCw size={14} className={regenerating ? 'animate-spin' : ''} />
              {regenerating ? 'Generando…' : 'Generar clave nueva'}
            </button>
            {!isSuperAdmin && (
              <p className="text-xs text-amber-600 mt-2">
                Solo el super-admin puede regenerar la clave.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Confirmación */}
      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-xl">
            <h4 className="font-bold text-gray-800 mb-2">Generar clave nueva</h4>
            <p className="text-sm text-gray-600 mb-5">
              La clave actual dejará de funcionar. Tendrás que pegar la nueva en el
              plugin de WordPress. ¿Continuar?
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirmOpen(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Cancelar
              </button>
              <button
                onClick={handleRegenerate}
                className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700"
              >
                Sí, generar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WordPressConnectionCard;
